import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Company, Resume, IAIHistory, PaginatedResponse, PaginationParams } from '../types';
import { applicationsService } from '../services/applicationsService';

import { generateAIData, getAIHistory } from '../services/aiService';
import { companiesService } from '../services/companiesService';
import { resumesService } from '../services/resumesService';
import { contactsService } from '../services/contactsService';

import LoadingSpinner from '../components/LoadingSpinner';
import AIHistoryList from '../components/AIHistoryList';

const UseAI = () => {
    const navigate = useNavigate();
    const [entityType, setEntityType] = useState<'company' | 'contact' | 'application'>('company');
    const [message, setMessage] = useState('');
    const [previewData, setPreviewData] = useState<any>(null);
    const [confidence, setConfidence] = useState<number | null>(null);
    const [createdEntity, setCreatedEntity] = useState<any>(null);

    // State for dropdowns
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [selectedContact, setSelectedContact] = useState<string>('');
    const [selectedResume, setSelectedResume] = useState<string>('');

    // Fetching data for dropdowns
    const { data: companiesResponse, isLoading: isLoadingCompanies } = useQuery<
        PaginatedResponse<Company>,
        Error
    >(
        ['companies', { limit: 100 }],
        async (context) => {
            const params = context.queryKey[1] as PaginationParams;
            return companiesService.getCompanies(params);
        },
        {
            select: (data) => data,
        }
    );
    const companies = companiesResponse?.data;
    const { data: resumesResponse, isLoading: isLoadingResumes } = useQuery<
        PaginatedResponse<Resume>,
        Error
    >(
        ['resumes', { limit: 100 }],
        async (context) => {
            const params = context.queryKey[1] as PaginationParams;
            return resumesService.getResumes(params);
        },
        {
            select: (data) => data,
        }
    );
    const resumes = resumesResponse?.data;

    const { data: contactsResponse, isLoading: isLoadingContacts } = useQuery<
        PaginatedResponse<any>,
        Error
    >(
        ['contacts', { limit: 100 }],
        async (context) => {
            const params = context.queryKey[1] as PaginationParams;
            return contactsService.getContacts(params);
        },
        {
            select: (data) => data,
            enabled: entityType === 'application' // Only fetch contacts when in application mode
        }
    );
    const contacts = contactsResponse?.data;
    const { data: aiHistoryResponse, refetch: refetchHistory } = useQuery<
        PaginatedResponse<IAIHistory>,
        Error
    >(
        ['aiHistory', { page: 1, limit: 10 }],
        async (context) => {
            const params = context.queryKey[1] as PaginationParams;
            return getAIHistory(params);
        },
        {
            select: (data) => data,
        }
    );
    const aiHistory = aiHistoryResponse?.data;

    const aiMutation = useMutation(generateAIData, {
        onSuccess: (response) => {
            setPreviewData(response.data);
            setConfidence(response.confidence);
            setCreatedEntity(null);
            toast.success('AI analysis complete!');
            refetchHistory();
        },
        onError: (error: any) => {
            toast.error(error.message || 'An error occurred during AI generation.');
        },
    });

    const createCompanyMutation = useMutation(companiesService.createCompany, {
        onSuccess: (company) => {
            toast.success('Company created successfully!');
            setCreatedEntity({ type: 'company', data: company });
            setPreviewData(null);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to create company.');
        },
    });

    const createContactMutation = useMutation(contactsService.createContact, {
        onSuccess: (contact) => {
            toast.success('Contact created successfully!');
            setCreatedEntity({ type: 'contact', data: contact });
            setPreviewData(null);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to create contact.');
        },
    });

    const createApplicationMutation = useMutation(applicationsService.createApplication, {
        onSuccess: (application) => {
            toast.success('Application created successfully!');
            setCreatedEntity({ type: 'application', data: application });
            setPreviewData(null);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to create application.');
        },
    });

    const handleGenerate = async () => {
        if (entityType === 'contact' && !selectedCompany) {
            toast.error('Please select a company for the contact.');
            return;
        }
        
        const payload: any = { type: entityType, message };
        
        if (entityType === 'application') {
            if (selectedResume) payload.resumeId = selectedResume;
            if (selectedCompany) payload.companyId = selectedCompany;
            if (selectedContact) payload.contactId = selectedContact;
        }

        if (entityType === 'contact') {
            if (selectedCompany) payload.companyId = selectedCompany;
        }
        
        aiMutation.mutate(payload);
    };

    const handleSave = async () => {
        if (!previewData) return;

        switch (entityType) {
            case 'company':
                createCompanyMutation.mutate({
                    name: previewData.companyName,
                    website: previewData.companyWebsite,
                    tier: previewData.priority?.toLowerCase() || 'medium',
                    location: previewData.location,
                    description: previewData.description,
                    notes: previewData.notes,
                    tags: previewData.tags
                });
                break;
            case 'contact':
                if (!selectedCompany) {
                    toast.error('A company must be selected to save a contact.');
                    return;
                }
                createContactMutation.mutate({
                    ...previewData,
                    company: selectedCompany,
                });
                break;
            case 'application':
                // Find the company ID from the name
                const company = companies?.find(c => c.name.toLowerCase() === previewData.companyName?.toLowerCase());
                if (!company) {
                    toast.error(`Company "${previewData.companyName}" not found. Please create it first.`);
                    return;
                }
                const resume = resumes?.find(r => r?.title?.toLowerCase() === previewData.resume?.toLowerCase());            
                if (!resume) {
                    toast.error(`Resume "${previewData.resume}" not found. Please create it first.`);
                    return;
                }
                const contact = contacts?.find(c => c.name.toLowerCase() === previewData.referralContact?.toLowerCase());
                if (!contact) {
                    toast.error(`Contact "${previewData.referralContact}" not found. Please create it first.`);
                    return;
                }
                createApplicationMutation.mutate({
                    role: previewData.jobTitle,
                    company: company._id,
                    applicationUrl: previewData.link,
                    status: previewData.status || 'applied',
                    notes: previewData.notes,
                    resume: resume._id,
                    referralContact: contact._id,
                    appliedDate: new Date().toISOString(),
                });
                break;
            default:
                toast.error('Invalid entity type.');
        }
    };

    const handleReuse = (messageToReuse: string, type: string, historyItem: {
        _id: string;
        type: string;
        message: string;
        aiResponse?: {
            companyName?: string;
            jobTitle?: string;
            resume?: string;
            referralContact?: string;
            link?: string;
        };
        createdAt: string;
    }) => {
        setMessage(messageToReuse);
        setEntityType(type as any);
        
        // Reset previous selections
        setSelectedResume('');
        setSelectedCompany('');
        setSelectedContact('');
        
        // Set related IDs if they exist in the history item
        if (historyItem.aiResponse) {
            const { aiResponse } = historyItem;
            
            // Set resume if it exists in the response
            if (aiResponse.resume && resumes) {
                const resume = resumes.find(r => r?.title === aiResponse.resume);
                if (resume) setSelectedResume(resume._id);
            }
            
            // Set company if it exists in the response
            if (aiResponse.companyName && companies) {
                const company = companies.find(c => c?.name === aiResponse.companyName);
                if (company) setSelectedCompany(company._id);
            }
            
            // Set contact if it exists in the response
            if (aiResponse.referralContact && contacts) {
                const contact = contacts.find(c => c?.name === aiResponse.referralContact);
                if (contact) setSelectedContact(contact._id);
            }
        }
    };

    useEffect(() => {
        setPreviewData(null);
        setConfidence(null);
        setCreatedEntity(null);
        setMessage('');
    }, [entityType]);

    const getCreatedEntityLink = () => {
        if (!createdEntity) return '';
        const { type, data } = createdEntity;
        switch (type) {
            case 'company': return `/companies/${data._id}`;
            case 'contact': return `/contacts/${data._id}`;
            case 'application': return `/applications/${data._id}`;
            default: return '/';
        }
    }


    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Use AI to Create Entries</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Input Section */}
                <div>
                    <div className="mb-4">
                        <label htmlFor="entityType" className="block text-sm font-medium text-gray-700">
                            Select what to create:
                        </label>
                        <select
                            id="entityType"
                            value={entityType}
                            onChange={(e) => setEntityType(e.target.value as any)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        >
                            <option value="company">Company</option>
                            <option value="contact">Contact</option>
                            <option value="application">Application</option>
                        </select>
                    </div>

                    {entityType === 'contact' && (
                        <div className="mb-4">
                            <label htmlFor="company" className="block text-sm font-medium text-gray-700">Company (Required)</label>
                            <select id="company" value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required>
                                <option value="">-- Select a Company --</option>
                                {isLoadingCompanies ? <option>Loading...</option> : companies?.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                            </select>
                        </div>
                    )}

                    {entityType === 'application' && (
                        <div>
                            <div className="mb-4">
                                <label htmlFor="resume" className="block text-sm font-medium text-gray-700">Attach Resume (Optional)</label>
                                <select id="resume" value={selectedResume} onChange={e => setSelectedResume(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                                    <option value="">-- Select a Resume --</option>
                                    {isLoadingResumes ? <option>Loading...</option> : resumes?.map(r => <option key={r._id} value={r._id}>{r.title}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label htmlFor="company" className="block text-sm font-medium text-gray-700">Company (Optional)</label>
                                <select id="company" value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required>
                                    <option value="">-- Select a Company --</option>
                                    {isLoadingCompanies ? <option>Loading...</option> : companies?.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                {/* Add contact */}
                                <label htmlFor="contact" className="block text-sm font-medium text-gray-700">Contact (Optional)</label>
                                <select id="contact" value={selectedContact} onChange={e => setSelectedContact(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                                    <option value="">-- Select a Contact --</option>
                                    {isLoadingContacts ? <option>Loading...</option> : contacts
                                        ?.filter(c => !selectedCompany || c.company?._id === selectedCompany)
                                        .map(c => <option key={c._id} value={c._id}>{c.name}</option>)
                                    }
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="mb-4">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                            Describe what you want to create:
                        </label>
                        <textarea
                            id="message"
                            rows={5}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder={`e.g., "I found a company called TechCorp, website is techcorp.com"`}
                        />
                    </div>

                    <button
                        onClick={handleGenerate}
                        disabled={aiMutation.isLoading || !message}
                        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 flex items-center justify-center"
                    >
                        {aiMutation.isLoading ? <LoadingSpinner /> : 'Generate with AI'}
                    </button>
                </div>

                {/* Preview Section */}
                <div>
                    <h2 className="text-xl font-semibold mb-2">Preview</h2>
                    <div className="bg-gray-50 p-4 rounded-lg min-h-[200px] border">
                        {aiMutation.isError && <div className="text-red-500">{(aiMutation.error as any)?.response?.data?.message || 'An error occurred'}</div>}

                        {previewData && (
                            <div>
                                {confidence && (
                                    <div className="mb-2 text-sm text-gray-600">
                                        Confidence: <span className="font-semibold">{(confidence * 100).toFixed(0)}%</span>
                                    </div>
                                )}
                                <pre className="whitespace-pre-wrap text-sm bg-white p-2 rounded">
                                    {JSON.stringify(previewData, null, 2)}
                                </pre>
                                <button
                                    onClick={handleSave}
                                    disabled={createCompanyMutation.isLoading || createContactMutation.isLoading || createApplicationMutation.isLoading}
                                    className="mt-4 w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
                                >
                                    {(createCompanyMutation.isLoading || createContactMutation.isLoading || createApplicationMutation.isLoading) ? <LoadingSpinner /> : 'Confirm and Save'}
                                </button>
                            </div>
                        )}

                        {createdEntity && (
                            <div>
                                <h3 className="font-semibold text-green-700">Successfully Created!</h3>
                                <div className="mt-2 p-2 bg-green-100 border border-green-200 rounded">
                                    <p><strong>Type:</strong> {createdEntity.type}</p>
                                    <p><strong>Name:</strong> {createdEntity.data.name || createdEntity.data.role || createdEntity.data.companyName}</p>
                                    <button
                                        onClick={() => navigate(getCreatedEntityLink())}
                                        className="mt-2 text-indigo-600 hover:underline"
                                    >
                                        View Details &rarr;
                                    </button>
                                </div>
                            </div>
                        )}

                        {!previewData && !createdEntity && !aiMutation.isLoading && <p className="text-gray-500">AI-generated data will appear here for your review.</p>}
                        {aiMutation.isLoading && <div className="flex justify-center items-center mt-4"><LoadingSpinner /> <span className="ml-2">AI is thinking...</span></div>}
                    </div>
                </div>
            </div>

            <div className="mt-8">
                <AIHistoryList history={aiHistory || []} onReuse={handleReuse} />
            </div>
        </div>
    );
};

export default UseAI; 